import React from 'react';
import { TextField, type TextFieldProps } from '../../base-fields/TextField';
import { useFormField } from '../../../useFormField';
import type { StripControlledProperties } from '../../../types';
import clsx from 'clsx';
import { TextInput } from '../../base-controls/text-controls/TextInput';
import { useMutation } from '~/util/http/useMutation';
import { useForm } from '~/shared/forms/FormContext';
import { Button } from '~/shared/components/buttons/Button';
import { Pencil } from 'lucide-react';

export const CalculatedTextFormField = React.memo(function CalculatedTextFormField(props: StripControlledProperties<TextFieldProps>) {
	const fieldProps = useFormField<string | null>(props);
	const { formHelpers } = useForm();

	if (!formHelpers.fieldCalculation) {
		throw new Error('CalculatedTextFormField must be used within a Form with fieldCalculation prop');
	}

	const calculateValues = useMutation(formHelpers.fieldCalculation.calculateMutation);

	const handleRecalculate = () => {
		const values = formHelpers.fieldCalculation?.transformValuesForCalculation
			? formHelpers.fieldCalculation.transformValuesForCalculation(props.name, formHelpers.values)
			: formHelpers.values;
		calculateValues.mutate(values, {
			onSuccess: data => {
				formHelpers.setFieldValue(props.name, data, true);
			}
		});
	};

	return (
		<div className="flex items-center gap-2">
			<TextField
				{...props}
				{...fieldProps}
				className={clsx(props.className, 'grow')}
				value={fieldProps.value ?? ''}
				type={props.type}
				onChange={fieldProps.onChange}
				onBlur={fieldProps.onBlur}
				customInput={
					<div className="flex items-center gap-1">
						<TextInput {...props.inputProps} />
						<Button
							Icon={Pencil}
							appearance="outline"
							onClick={handleRecalculate}
							isPending={calculateValues.isPending}
							label="Calculate"
						/>
					</div>
				}
			/>
		</div>
	);
});
